import React from 'react';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router';

import { InfoError, ModalError } from 'src/componentes';
import {
    clienteApi,
    ErrorApi,
    ErrorConDetalles,
    ErrorHttp,
    ErrorTipoRespuesta,
    tokenStorage,
} from './api';
import { Contexto } from './contexto';
import { DescriptorDeUsuario } from './descriptor';
import Login from './paginas/Login';
import PaginaPrincipal from './paginas/PaginaPrincipal';

interface Props extends RouteComponentProps {
    /** Función que será llamada cuando la aplicación termine de montarse. */
    alMontarse?(): void;
}

interface State {
    readonly usuarioActual?: DescriptorDeUsuario;
    readonly error?: InfoError;
}

class App extends React.Component<Props, State> {
    public state: State = {};

    public componentDidMount() {
        const { alMontarse = () => undefined } = this.props;

        this.intentarAutenticarDesdeToken()
            .then(alMontarse)
            .catch(error => {
                this.mostrarModalError(error);
                alMontarse();
            });
    }

    public render() {
        const { usuarioActual, error } = this.state;

        return (
            <>
                {error && (
                    <ModalError
                        mensaje={error.mensaje}
                        detalles={error.detalles}
                        alClickearEntendido={this.cerrarModalError}
                    />
                )}

                <Switch>
                    <Route
                        path='/login'
                        render={props => (
                            <Login
                                {...props}
                                autenticar={this.autenticar}
                                cambiarContraseñaYReautenticar={this.cambiarContraseñaYReautenticar}
                            />
                        )}
                    />
                    <Route
                        render={() =>
                            usuarioActual ? (
                                <Contexto.Provider
                                    value={{
                                        usuarioActual,
                                        mostrarModalError: this.mostrarModalError,
                                    }}
                                >
                                    <PaginaPrincipal
                                        usuarioActual={usuarioActual}
                                        alCerrarSesion={this.cerrarSesion}
                                    />
                                </Contexto.Provider>
                            ) : null
                        }
                    />
                </Switch>
            </>
        );
    }

    private async intentarAutenticarDesdeToken() {
        const tokenActual = tokenStorage.get();

        if (tokenActual) {
            // si hay token intentar autenticar con él
            const descriptor = await clienteApi
                .ejecutar('descriptorDeUsuarioDesdeToken', tokenActual)
                .catch(() => undefined); // hace nada si falla la autenticación por token
            if (descriptor) {
                this.setState({
                    usuarioActual: new DescriptorDeUsuario(descriptor),
                });
                return; // si todo sale bien, salir de esta función.
            }
        }

        // Aquí se llega ya sea porque no hay token o porque la autenticación por token falló.
        // En cualquiera de los casos enviar a la página de login para obtener un nuevo token.
        tokenStorage.clear();
        this.props.history.replace('/login', { rutaOrigen: this.props.location });
    }

    private readonly autenticar = async (usuario: string, contraseña: string) => {
        try {
            const resultados = await clienteApi.ejecutar('autenticarYObtenerDescriptor', {
                usuario,
                contraseña,
            });

            if (!resultados.cambioDeContraseñaRequerido) {
                tokenStorage.set(resultados.token);
                this.setState({
                    usuarioActual: new DescriptorDeUsuario(resultados),
                });
                return true;
            }
            return false;
        } catch (error) {
            if (error instanceof ErrorHttp) {
                throw transformarErrorHttp(error);
            }
            throw error;
        }
    };

    private readonly cambiarContraseñaYReautenticar = async (
        usuario: string,
        contraseñaActual: string,
        nuevaContraseña: string,
    ) => {
        await clienteApi.ejecutar('cambiarContraseña', {
            usuario,
            contraseñaActual,
            nuevaContraseña,
        });

        // Intenta una autenticación completa otra vez. Esto es más lento para el usuario pero
        // más fácil para nosotros. Ocurre solo al cambiar la contraseña la primera vez.
        await this.autenticar(usuario, nuevaContraseña);
    };

    private readonly cerrarSesion = () => {
        tokenStorage.clear();
        this.setState({ usuarioActual: undefined });
        this.props.history.push('/login');
    };

    private readonly mostrarModalError = (
        error:
            | string
            | ErrorConDetalles
            | ErrorApi
            | ErrorHttp
            | ErrorTipoRespuesta
            | Error
            | InfoError,
    ) => {
        if (typeof error === 'string') {
            this.setState({ error: { mensaje: error } });
        } else if (error instanceof ErrorConDetalles) {
            this.setState({
                error: { mensaje: error.mensajeParaUsuario, detalles: error.detalles },
            });
        } else if (error instanceof ErrorHttp) {
            const e = transformarErrorHttp(error);
            this.setState({ error: { mensaje: e.mensajeParaUsuario, detalles: e.detalles } });
        } else if (error instanceof ErrorTipoRespuesta || error instanceof ErrorApi) {
            this.setState({ error: { mensaje: error.mensaje, detalles: error.detalles } });
        } else if (error instanceof Error) {
            // La función fetcher coloca esta propiedad si el error fue lanzado por
            // la función window.fetch
            if ((error as { origen?: string }).origen === 'fetch') {
                this.setState({
                    error: {
                        mensaje:
                            'No fue posible comunicarse con el servidor. Posiblemente no' +
                            ' tenga conexión a internet.',
                        detalles: [error.message],
                    },
                });
            } else {
                // es una instancia de Error cualquiera
                this.setState({
                    error: { mensaje: 'Ocurrió un error desconocido.', detalles: [error.message] },
                });
            }
        } else if (typeof (error as unknown) === 'object') {
            // Es un objeto con detalles del error
            this.setState({ error: { mensaje: error.mensaje, detalles: error.detalles } });
        } else {
            // Se descartaron todas las opciones anteriores. Aún existe la posibilidad de que
            // error sea un primitivo o undefined.
            this.setState({
                error: { mensaje: 'Ocurrió un error desconocido.', detalles: [`${error}`] },
            });
        }
    };

    private readonly cerrarModalError = () => {
        this.setState({ error: undefined });
    };
}

function transformarErrorHttp(error: ErrorHttp) {
    const mensaje =
        'Ocurrió un error mientras se comunicaba con el servidor. Se logró' +
        ' establecer comunicación pero se obtuvieron datos incorrectos.' +
        (error.codigo >= 400 && error.codigo < 500
            ? ' Esto es posiblemente un error en el sistema. Contacte al administrador.'
            : error.codigo >= 500 && error.codigo < 600
            ? ' Parece que el servidor no responde o tiene algún problema.' +
              ' Reinténtelo luego de unos segundos o contacte al administrador.'
            : '');
    return new ErrorConDetalles(mensaje, [error.message]);
}

export default withRouter(App);



// WEBPACK FOOTER //
// ./src/App.tsx